import {
  confirmSignUp as amplifyConfirmSignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmResetPassword,
  fetchAuthSession,
  resetPassword,
} from "@aws-amplify/auth";
import * as AmplifyAuth from "@aws-amplify/auth";
import { Amplify } from "aws-amplify";
import { getCognitoConfig } from "../../config/cognito";

const config = getCognitoConfig();

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.userPoolId,
      userPoolClientId: config.clientId,
      loginWith: {
        email: true,
      },
      signUpVerificationMethod: "code",
      userAttributes: {
        email: {
          required: true,
        },
      },
      passwordFormat: {
        minLength: 8,
        requireLowercase: true,
        requireUppercase: true,
        requireNumbers: true,
        requireSpecialCharacters: true,
      },
    },
  },
});

export const signIn = async (username: string, password: string) => {
  try {
    const result = await amplifySignIn({ username, password });

    // In Amplify v6, unconfirmed users return a nextStep with signInStep === 'CONFIRM_SIGN_UP'
    // instead of throwing UserNotConfirmedException
    if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
      console.log(
        "User not confirmed (detected via nextStep), redirecting to verification",
      );
      const userNotConfirmedError = new Error(
        "Your email is not verified. You will be redirected to verify your account.",
      );
      (userNotConfirmedError as any).name = "UserNotConfirmedException";
      (userNotConfirmedError as any).username = username;
      throw userNotConfirmedError;
    }

    return result;
  } catch (error: any) {
    // Handle "User is already authenticated" error
    if (
      error.name === "UserAlreadyAuthenticatedException" ||
      error.message?.includes("already authenticated")
    ) {
      console.log("User already authenticated, signing out first...");
      await amplifySignOut();
      // Retry sign in after sign out
      const retryResult = await amplifySignIn({ username, password });

      // Check again for unconfirmed user after retry
      if (retryResult.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        console.log(
          "User not confirmed (detected via nextStep after retry), redirecting to verification",
        );
        const userNotConfirmedError = new Error(
          "Your email is not verified. You will be redirected to verify your account.",
        );
        (userNotConfirmedError as any).name = "UserNotConfirmedException";
        (userNotConfirmedError as any).username = username;
        throw userNotConfirmedError;
      }

      return retryResult;
    }

    // Handle "User is not confirmed" error - this means email is not verified
    // This is a fallback in case the error is thrown instead of returned via nextStep
    if (
      error.name === "UserNotConfirmedException" ||
      error.message?.includes("not confirmed") ||
      error.message?.includes("not verified")
    ) {
      console.log(
        "User not confirmed (detected via exception), redirecting to verification",
      );
      // Re-throw with a more user-friendly message
      const userFriendlyError = new Error(
        "Your email is not verified. You will be redirected to verify your account.",
      );
      (userFriendlyError as any).name = "UserNotConfirmedException";
      (userFriendlyError as any).username = username;
      throw userFriendlyError;
    }

    throw error;
  }
};

export const signUp = async (
  email: string,
  password: string,
  name?: string,
) => {
  const userAttributes = name ? { email, name } : { email };
  const result = await amplifySignUp({
    username: email,
    password,
    options: {
      userAttributes,
    },
  });
  return result;
};

// Optional helper: sign up and persist chosen product type for later checkout
export const signUpWithProduct = async (
  email: string,
  password: string,
  name: string | undefined,
  productType: string | null,
) => {
  const result = await signUp(email, password, name);
  if (productType) {
    localStorage.setItem("stripe_product_type", productType);
  }
  return result;
};

export const confirmSignUp = async (username: string, code: string) => {
  const result = await amplifyConfirmSignUp({
    username,
    confirmationCode: code,
  });
  return result;
};

export const resendConfirmationCode = async (username: string) => {
  try {
    // Try to use the already-imported module namespace if it has the function
    const namespace = AmplifyAuth as unknown as {
      resendSignUpCode?: (input: { username: string }) => Promise<any>;
    };
    if (typeof namespace.resendSignUpCode === "function") {
      return await namespace.resendSignUpCode({ username });
    }

    // Fallback: dynamic import to avoid bundler export issues
    const mod = await import("@aws-amplify/auth");
    const fn = (mod as any).resendSignUpCode || (mod as any).resendSignUp;
    if (typeof fn !== "function") {
      throw new Error("resendSignUpCode not available in Amplify auth module");
    }

    return await fn({ username });
  } catch (err: any) {
    throw new Error(
      `Failed to resend confirmation code: ${err?.message || String(err)}`,
    );
  }
};

export const forgotPassword = async (username: string) => {
  const result = await resetPassword({ username });
  return result;
};

export const confirmForgotPassword = async (
  username: string,
  confirmationCode: string,
  newPassword: string,
) => {
  const result = await confirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  });
  return result;
};

export const getValidAccessToken = async (): Promise<string | null> => {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.toString() || null;
};

export const signOut = async () => {
  await amplifySignOut();
};

export const getRefreshToken = async (): Promise<string | null> => {
  try {
    // Method 2: Check localStorage (Amplify stores tokens here)
    const cognitoKeys = Object.keys(localStorage).filter(
      (key) =>
        key.includes("CognitoIdentityServiceProvider") &&
        key.includes("refreshToken"),
    );

    if (cognitoKeys.length > 0) {
      const refreshToken = localStorage.getItem(cognitoKeys[0]);
      return refreshToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting refresh token:", error);
    return null;
  }
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
) => {
  const { updatePassword } = await import("@aws-amplify/auth");
  const result = await updatePassword({
    oldPassword,
    newPassword,
  });
  return result;
};

export const updateUserAttribute = async (
  attributeKey: string,
  value: string,
) => {
  const { updateUserAttribute: amplifyUpdateUserAttribute } = await import(
    "@aws-amplify/auth"
  );
  const result = await amplifyUpdateUserAttribute({
    userAttribute: {
      attributeKey,
      value,
    },
  });
  return result;
};
